import { httpBatchLink, httpLink, splitLink } from "@trpc/react-query";
import { createTRPCNext } from "@trpc/next";

import { AppRouter } from "../server";
import { mutationOptions, queryClient, queryOptions } from "./queryClient";

type Config = {
  url: string;
  getSession: () => { email: string; token: string } | null;
  getToken: () => Promise<string | null>;
  getTzOffset?: () => number;
  onError?: (error: any) => Promise<void>;
  version: string;
};

export const getTrpcClient = ({
  url,
  getSession,
  getToken,
  version,
  onError,
  getTzOffset,
}: Config) => {
  const config = {
    url: `${url}/api/trpc`,
    async headers() {
      const token = await getToken();
      return {
        ...(token ? { authorization: token } : {}),
        email: getSession()?.email,
        token: getSession()?.token,
        "api-version": version,
        "tz-offset": `${getTzOffset?.() || 0}`,
      };
    },
  };

  const defaultOptions = queryClient.getDefaultOptions();
  queryClient.setDefaultOptions({
    ...defaultOptions,
    queries: {
      ...defaultOptions.queries,
      ...queryOptions,
    },
    mutations: {
      ...defaultOptions.mutations,
      ...mutationOptions,
      onError: async (error: any) => {
        await onError?.(error);
      },
    },
  });

  return createTRPCNext<AppRouter>({
    config() {
      return {
        queryClient,
        links: [
          splitLink({
            condition(op) {
              return op.context.skipBatch === true;
            },
            true: httpLink(config),
            false: httpBatchLink(config),
          }),
        ],
      };
    },
    ssr: false,
  });
};

export let trpc = getTrpcClient({
  url: "http://localhost:3000",
  getSession: () => {
    return null;
  },
  getToken: async () => {
    return null;
  },
  getTzOffset: () => {
    return 0;
  },
  version: "latest",
});

export const setTrpcClient = (client: ReturnType<typeof getTrpcClient>) => {
  trpc = client;
};
